/**
 * Contingency catalog — the single source of truth for the contingencies a
 * buyer may attach to an offer.
 *
 * Every surface that renders or labels contingencies reads from here (offer
 * wizard Terms/Review steps, wizard summary, offer card, counter-offer modal,
 * negotiation workspace counter form) so the list can never drift again. The
 * keys are the property names stored in the offer's `contingencies` JSON dict
 * and must stay in sync with `backend/app/models/offer_contingencies.py`.
 *
 * Availability
 * ------------
 * Most contingencies apply to any listing. A handful only make sense for
 * certain properties — you cannot inspect a well the house does not have — so
 * those declare an `appliesTo` predicate evaluated against facts derived from
 * the listing's `mls_profile` blob (see `derivePropertyFacts`).
 *
 * Nothing here is pre-selected: buyers opt in. See `getContingencyOptions`.
 *
 * Vacant land
 * -----------
 * A listing whose `paperwork_category` is `vacant_land` uses the vacant land
 * offer (docs/legal/states/WI/paperwork-categories-review.md §5). It offers
 * only what that offer prints: the land-only contingencies (zoning, land
 * division, access, soil, water, utilities, environmental, wetlands) plus
 * survey, septic, well and financing, with well and septic worded for land.
 * The home keys (inspection, radon, lead paint, pest, insurance, association
 * review, appraisal, sale of home, title) are never offered there, and the
 * server refuses them with a 422 (`offer_contingencies_invalid`), mirroring
 * `CONTINGENCY_CLAUSES` in backend/app/models/offer_contingencies.py.
 *
 * Deadlines
 * ---------
 * Every contingency may carry a negotiated deadline `<key>_days` (whole days
 * after acceptance, 1–365) with no default. On every Wisconsin offer (vacant
 * land, residential and condo) it is REQUIRED for each selected contingency
 * (template 7(a); legal review §9.7; user decision for home and condo offers):
 * see `missingDeadlineKeys`. Elsewhere it stays optional.
 *
 * Lead-based paint election
 * -------------------------
 * On a Wisconsin residential or condo listing built before 1978, or whose year
 * built is unknown, the buyer must choose: keep the federal inspection
 * opportunity (`lead_paint_election: 'inspect'` with `lead_paint_days`) or
 * waive it (`lead_paint_election: 'waive'`). The election replaces the
 * lead-paint contingency card there, and is never sent anywhere else. See
 * `leadPaintElectionMode`.
 */

import { copyText } from '../SellerDashboard/paperworkCopy';

/**
 * `water_source` values from the seller's MLS profile that mean a private or
 * shared well rather than a municipal main.
 * @see frontend/src/components/SellerDashboard/mlsFieldSchema.js
 */
const WELL_WATER_SOURCES = ['well', 'shared well'];

/**
 * `sewer` values that mean an on-site waste system the buyer can have pumped
 * and inspected. "Mound" and "Holding Tank" are septic variants, not mains.
 */
const ON_SITE_SEWER_SYSTEMS = ['septic', 'mound', 'holding tank'];

/** Federal ban on lead-based paint in residential construction (24 CFR 35). */
const LEAD_PAINT_BAN_YEAR = 1978;

/** The paperwork category of listings that use the vacant land offer. */
export const VACANT_LAND_CATEGORY = 'vacant_land';
export const RESIDENTIAL_CATEGORY = 'residential';
export const CONDO_CATEGORY = 'condo';
/** Categories that use a Wisconsin home offer (residential or condo). */
const HOME_CATEGORIES = [RESIDENTIAL_CATEGORY, CONDO_CATEGORY];
/** The only state with paperwork categories today. */
const PAPERWORK_STATE = 'WI';

/** A contingency's negotiated deadline key, e.g. `soil_evaluation_days`. */
export const DEADLINE_SUFFIX = '_days';
export const MIN_DEADLINE_DAYS = 1;
export const MAX_DEADLINE_DAYS = 365;

const CONTINGENCY_GROUPS = [
  { key: 'standard', label: 'Core protections' },
  { key: 'land', label: 'Land & site' },
  { key: 'property', label: 'Property condition & systems' },
  { key: 'transaction', label: 'Title, insurance & timing' },
];

/**
 * Ordered catalog. `title` is the long form used on wizard cards, `label` the
 * short form used in chips, checkboxes and summaries, and `description` the
 * buyer-facing explanation of what the contingency actually protects.
 *
 * `categories` limits an entry to listings of those paperwork categories
 * (land-only keys); `excludeCategories` hides it on them (home keys the land
 * offer doesn't print). `land` replaces the wording on a vacant land listing.
 */
const CONTINGENCY_CATALOG = [
  {
    key: 'inspection',
    group: 'standard',
    excludeCategories: [VACANT_LAND_CATEGORY],
    label: 'Home inspection',
    title: 'Inspection contingency',
    description:
      'Allows you to back out if the home inspection reveals significant issues like structural damage, mold, or faulty systems.',
  },
  {
    key: 'appraisal',
    excludeCategories: [VACANT_LAND_CATEGORY],
    group: 'standard',
    label: 'Appraisal',
    title: 'Appraisal contingency',
    description:
      'Protects you if the home appraises for less than the purchase price, ensuring you are not overpaying.',
  },
  {
    key: 'financing',
    group: 'standard',
    label: 'Financing',
    title: 'Financing contingency',
    description:
      'Allows you to withdraw if you are unable to secure a mortgage or financing falls through.',
    land: {
      description:
        "Lets you end the offer, by written notice before the deadline, if you can't get a written loan commitment for the land.",
    },
  },
  {
    key: 'well',
    group: 'property',
    label: 'Well inspection',
    title: 'Well inspection contingency',
    description:
      'Lets you withdraw if the well fails a flow-rate or water-quality test for bacteria, nitrates, or arsenic. Offered because this listing draws its water from a well.',
    appliesTo: (facts) => facts.hasWell,
    land: {
      label: 'Existing well test',
      title: 'Existing well contingency',
      description:
        "Lets you have the existing well's water tested by a certified lab, and its flow tested, and end the offer by written notice before the deadline if the results aren't acceptable. Offered because this listing reports a well.",
    },
  },
  {
    key: 'septic',
    group: 'property',
    label: 'Septic inspection',
    title: 'Septic inspection contingency',
    description:
      'Lets you withdraw if the septic system fails a pump-and-inspect or the drain field needs replacement. Offered because this listing is not on a municipal sewer.',
    appliesTo: (facts) => facts.hasOnSiteSewer,
    land: {
      label: 'Existing septic system',
      title: 'Existing private sewage system contingency',
      description:
        "Lets you have the existing private sewage (septic) system evaluated and end the offer, by written notice before the deadline, if it's failing, doesn't meet code, or doesn't suit your planned use. Offered because this listing reports an on-site system.",
    },
  },
  {
    key: 'radon',
    group: 'property',
    excludeCategories: [VACANT_LAND_CATEGORY],
    label: 'Radon test',
    title: 'Radon test contingency',
    description:
      'Lets you withdraw or ask for mitigation if a radon test comes back at or above the EPA action level of 4.0 pCi/L.',
  },
  {
    key: 'pest',
    excludeCategories: [VACANT_LAND_CATEGORY],
    group: 'property',
    label: 'Pest inspection',
    title: 'Wood-destroying pest contingency',
    description:
      'Lets you withdraw if an inspection finds termites, carpenter ants, or other wood-destroying organisms and the damage is not repaired.',
  },
  {
    key: 'lead_paint',
    group: 'property',
    excludeCategories: [VACANT_LAND_CATEGORY],
    label: 'Lead-based paint',
    title: 'Lead-based paint contingency',
    description:
      'Gives you a risk assessment window for lead-based paint. Offered because this home was built before the 1978 federal ban.',
    appliesTo: (facts) => facts.builtBeforeLeadPaintBan,
  },
  {
    key: 'survey',
    group: 'property',
    label: 'Survey',
    title: 'Survey contingency',
    description:
      'Lets you withdraw if a boundary survey shows an encroachment, a boundary gap or overlap, no legal access, or less land than described.',
  },
  {
    key: 'title',
    excludeCategories: [VACANT_LAND_CATEGORY],
    group: 'transaction',
    label: 'Clear title',
    title: 'Title contingency',
    description:
      'Lets you withdraw if the title search finds liens, unresolved claims, or anything else that stops the seller conveying clear title.',
  },
  {
    key: 'insurance',
    excludeCategories: [VACANT_LAND_CATEGORY],
    group: 'transaction',
    label: 'Insurability',
    title: 'Homeowner’s insurance contingency',
    description:
      'Lets you withdraw if you cannot obtain homeowner’s insurance on the property at a reasonable cost.',
  },
  {
    key: 'hoa_review',
    excludeCategories: [VACANT_LAND_CATEGORY],
    group: 'transaction',
    label: 'HOA document review',
    title: 'HOA document review contingency',
    description:
      'Gives you a window to review the HOA bylaws, budget, and pending assessments. Offered because this listing belongs to an association.',
    appliesTo: (facts) => facts.hasHoa,
  },
  {
    key: 'sale_of_home',
    excludeCategories: [VACANT_LAND_CATEGORY],
    group: 'transaction',
    label: 'Sale of current home',
    title: 'Sale-of-current-home contingency',
    description:
      'Lets you withdraw if your existing home does not sell in time to fund this purchase. Sellers weigh this heavily, so only add it if you need it.',
  },
  // ── Vacant land only (paperwork-categories-review.md §5.2, clauses 3–11).
  // Plain-language labels: the backend catalog has the keys but no labels.
  {
    key: 'land_use_approval',
    group: 'land',
    categories: [VACANT_LAND_CATEGORY],
    label: 'Zoning & land-use approval',
    title: 'Zoning and land-use approval contingency',
    description:
      "Lets you end the offer, by written notice before the deadline, if the zoning doesn't allow your planned use and you can't get the approvals it needs, such as a rezoning, conditional use permit, variance or building permit.",
  },
  {
    key: 'land_division',
    group: 'land',
    categories: [VACANT_LAND_CATEGORY],
    label: 'Land division approval',
    title: 'Land division contingency',
    description:
      "If the land is being split off from a larger parcel, makes the sale depend on approval and recording of the certified survey map or plat before closing. If approval isn't obtained by the deadline, you or the seller may end the offer by written notice.",
  },
  {
    key: 'access',
    group: 'land',
    categories: [VACANT_LAND_CATEGORY],
    label: 'Road access',
    title: 'Access contingency',
    description:
      "Lets you end the offer, by written notice before the deadline, if the land has no legal access to a public road (by road frontage or a recorded easement) or a driveway permit for your planned use isn't available.",
  },
  {
    key: 'soil_evaluation',
    group: 'land',
    categories: [VACANT_LAND_CATEGORY],
    label: 'Soil & septic site evaluation',
    title: 'Soil and site evaluation contingency',
    description:
      "Lets you end the offer, by written notice before the deadline, if a Wisconsin certified soil tester's evaluation shows the land can't support a private onsite wastewater treatment system (POWTS, commonly called a septic system) suitable for your planned use.",
  },
  {
    key: 'water_supply',
    group: 'land',
    categories: [VACANT_LAND_CATEGORY],
    label: 'Water supply',
    title: 'Water supply contingency',
    description:
      "Lets you end the offer, by written notice before the deadline, if you can't confirm a safe water supply for your planned use: either a private well that can reasonably be built, or an available public water connection and its cost.",
  },
  {
    key: 'utilities',
    group: 'land',
    categories: [VACANT_LAND_CATEGORY],
    label: 'Utilities available',
    title: 'Utilities contingency',
    description:
      "Lets you end the offer, by written notice before the deadline, if a utility you select (electricity, natural gas, phone, internet, public water or public sewer) isn't available, or connecting costs more than the amount you set.",
  },
  {
    key: 'environmental',
    group: 'land',
    categories: [VACANT_LAND_CATEGORY],
    label: 'Environmental assessment',
    title: 'Environmental contingency',
    description:
      'Lets you order a Phase I environmental site assessment (and a Phase II if it recommends one) and end the offer, by written notice before the deadline, if it finds contamination, buried waste or fill, a dump site, an underground storage tank, or a contaminated-site listing.',
  },
  {
    key: 'wetlands_floodplain',
    group: 'land',
    categories: [VACANT_LAND_CATEGORY],
    label: 'Wetlands, shoreland & floodplain',
    title: 'Wetlands, shoreland and floodplain contingency',
    description:
      'Lets you order a wetland delineation and shoreland, floodplain and navigable-water determinations, and end the offer, by written notice before the deadline, if they leave too little usable area for your planned use.',
  },
];

const CONTINGENCY_BY_KEY = CONTINGENCY_CATALOG.reduce((acc, entry) => {
  acc[entry.key] = entry;
  return acc;
}, {});

const CONTINGENCY_KEYS = CONTINGENCY_CATALOG.map((entry) => entry.key);

/** Keys offered only on vacant land listings (backend LAND_ONLY_CONTINGENCY_KEYS). */
export const LAND_ONLY_CONTINGENCY_KEYS = CONTINGENCY_CATALOG.filter((entry) =>
  (entry.categories || []).includes(VACANT_LAND_CATEGORY),
).map((entry) => entry.key);

/** Keys that take a `<key>_days` deadline: all of them (backend CONTINGENCY_DEADLINE_KEYS). */
export const DEADLINE_CONTINGENCY_KEYS = [...CONTINGENCY_KEYS];

const hasKey = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** Lower-cased, trimmed string for comparing free-form MLS select values. */
const normalize = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

/**
 * Derive the property facts that decide which conditional contingencies apply.
 *
 * Reads the seller-entered `mls_profile` blob, falling back to the promoted
 * top-level `year_built` column when the profile omits it. Anything the seller
 * left blank is treated as "not present" — we would rather hide an option than
 * offer a well inspection on a house wired to a municipal main.
 *
 * @param {Object|null|undefined} property - House record (detail shape).
 * @returns {{hasWell: boolean, hasOnSiteSewer: boolean, hasHoa: boolean,
 *   builtBeforeLeadPaintBan: boolean, waterSourceKnown: boolean,
 *   sewerKnown: boolean}}
 */
export const derivePropertyFacts = (property) => {
  const profile =
    property && typeof property.mls_profile === 'object' && property.mls_profile
      ? property.mls_profile
      : {};

  const waterSource = normalize(profile.water_source);
  const sewer = normalize(profile.sewer);
  const hoa = normalize(profile.hoa);
  const yearBuilt = Number(profile.year_built ?? property?.year_built);

  return {
    hasWell: WELL_WATER_SOURCES.includes(waterSource),
    hasOnSiteSewer: ON_SITE_SEWER_SYSTEMS.includes(sewer),
    hasHoa: hoa !== '' && hoa !== 'none',
    builtBeforeLeadPaintBan:
      Number.isFinite(yearBuilt) && yearBuilt > 0 && yearBuilt < LEAD_PAINT_BAN_YEAR,
    // Drive the "we could not tell" hint in the wizard: without utility data we
    // silently hide well/septic, which is exactly the T-39 complaint. Tracked
    // per field, because the two are independent — a listing can state its
    // water source and leave sewer blank, and an OR here would suppress the
    // hint while still hiding the septic option.
    waterSourceKnown: waterSource !== '',
    sewerKnown: sewer !== '',
  };
};

/**
 * Names of the contingencies withheld purely because the listing does not say
 * whether they apply, so the wizard can tell the buyer which option is missing
 * rather than leaving a silent gap (T-39).
 *
 * @param {Object|null|undefined} property - House record.
 * @returns {Array<string>} Short labels, e.g. `['Septic inspection']`.
 */
export const getUndeterminedContingencyLabels = (property) => {
  const facts = derivePropertyFacts(property);
  const missing = [];
  if (!facts.waterSourceKnown) missing.push(getContingencyLabel('well'));
  if (!facts.sewerKnown) missing.push(getContingencyLabel('septic'));
  return missing;
};

/**
 * The listing's paperwork category: the house's `paperwork_category`, else its
 * `paperwork` summary's category, else null (unknown).
 *
 * @param {Object|null|undefined} property - House record.
 * @returns {string|null}
 */
export const getPaperworkCategory = (property) =>
  property?.paperwork_category || property?.paperwork?.category || null;

/** True when the listing uses the vacant land offer. */
export const isVacantLandProperty = (property) =>
  getPaperworkCategory(property) === VACANT_LAND_CATEGORY;

/**
 * The listing's state code (upper case), from the property or, when it isn't
 * loaded, the offer's `property_state`; null when unknown.
 */
const stateOf = (property, offer) => {
  const raw = property?.state || property?.property_state || offer?.property_state || '';
  const code = String(raw).trim().toUpperCase();
  return code || null;
};

/**
 * True when the offer is a Wisconsin residential or condo offer. Paperwork
 * categories exist only in Wisconsin, so a home category with no known state
 * counts; a Wisconsin listing with no category yet is the residential offer
 * (the server's default).
 */
const usesWisconsinHomeOffer = (category, state) => {
  if (state && state !== PAPERWORK_STATE) return false;
  if (HOME_CATEGORIES.includes(category)) return true;
  return state === PAPERWORK_STATE && !category;
};

/** Every selected contingency needs a deadline: vacant land, and WI home and condo offers. */
const deadlinesRequiredFor = (category, state) =>
  category === VACANT_LAND_CATEGORY || usesWisconsinHomeOffer(category, state);

/**
 * True when the listing's offer is a Wisconsin residential or condo offer:
 * every selected contingency needs a deadline, and legal's deadline note
 * (`WI.offer.contingency_deadline.note`) is shown.
 *
 * @param {Object|null|undefined} property - House record (may be null).
 * @param {Object|null} [offer] - The offer being responded to, if any.
 * @returns {boolean}
 */
export const isWisconsinHomeOffer = (property, offer = null) =>
  usesWisconsinHomeOffer(
    offer ? categoryForOffer(offer, property) : getPaperworkCategory(property),
    stateOf(property, offer),
  );

// ── Lead-based paint election (WI residential and condo) ─────────────────

export const LEAD_PAINT_KEY = 'lead_paint';
export const LEAD_PAINT_ELECTION_KEY = 'lead_paint_election';
export const LEAD_PAINT_INSPECT = 'inspect';
export const LEAD_PAINT_WAIVE = 'waive';
/** Prefilled when the buyer chooses to inspect: the federal standard period. */
export const LEAD_PAINT_DEFAULT_DAYS = 10;
/** The listing was built before 1978 (`WI.offer.lead_paint.help`). */
export const LEAD_PAINT_MODE_PRE_1978 = 'pre_1978';
/** The listing doesn't say (`WI.offer.lead_paint.help.unknown_year`). */
export const LEAD_PAINT_MODE_UNKNOWN_YEAR = 'unknown_year';

/** Legal's lead-paint election copy (copy file §10, `WI.offer.lead_paint.*`). */
export const LEAD_PAINT_COPY = {
  title: copyText('WI.offer.lead_paint.title'),
  question: copyText('WI.offer.lead_paint.question'),
  help: copyText('WI.offer.lead_paint.help'),
  helpUnknownYear: copyText('WI.offer.lead_paint.help.unknown_year'),
  optionInspect: copyText('WI.offer.lead_paint.option.inspect'),
  optionWaive: copyText('WI.offer.lead_paint.option.waive'),
  daysLabel: copyText('WI.offer.lead_paint.days_label'),
  daysHelp: copyText('WI.offer.lead_paint.days_help'),
  waiveNote: copyText('WI.offer.lead_paint.waive_note'),
  missing: copyText('WI.offer.lead_paint.missing'),
  summaryInspect: (days) => copyText('WI.offer.lead_paint.summary.inspect', { days }),
  summaryWaive: copyText('WI.offer.lead_paint.summary.waive'),
};

/**
 * The listing's year built: the top-level `year_built`, else
 * `mls_profile.year_built` (the server's order); null when neither is a
 * positive year.
 */
export const getListingYearBuilt = (property) => {
  const profile =
    property && typeof property.mls_profile === 'object' && property.mls_profile ? property.mls_profile : {};
  const candidates = [property?.year_built, profile.year_built];
  for (const value of candidates) {
    if (value === null || value === undefined || value === '') continue;
    const year = Number(value);
    if (Number.isFinite(year) && year > 0) return year;
  }
  return null;
};

/** The election recorded on an offer or form (`inspect` | `waive`), or null. */
export const getLeadPaintElection = (contingencies) => {
  const value =
    contingencies && typeof contingencies === 'object' && !Array.isArray(contingencies)
      ? contingencies[LEAD_PAINT_ELECTION_KEY]
      : null;
  return value === LEAD_PAINT_INSPECT || value === LEAD_PAINT_WAIVE ? value : null;
};

/**
 * Whether the buyer must make the lead-based paint election, and which help
 * text applies: `pre_1978`, `unknown_year`, or null (not asked, never sent).
 *
 * Asked on a Wisconsin residential or condo offer when the listing was built
 * before 1978 or doesn't say. Never on vacant land or a 1978+ home. When
 * responding to an offer whose listing isn't loaded, it is asked only if the
 * offer already carries an election.
 *
 * @param {Object|null|undefined} property - House record (may be null).
 * @param {Object|null} [offer] - The offer being responded to, if any.
 * @returns {string|null}
 */
export const leadPaintElectionMode = (property, offer = null) => {
  if (!isWisconsinHomeOffer(property, offer)) return null;
  if (!property) {
    return offer && getLeadPaintElection(offer.contingencies) ? LEAD_PAINT_MODE_PRE_1978 : null;
  }
  const year = getListingYearBuilt(property);
  if (year === null) return LEAD_PAINT_MODE_UNKNOWN_YEAR;
  return year < LEAD_PAINT_BAN_YEAR ? LEAD_PAINT_MODE_PRE_1978 : null;
};

/**
 * Form state for the election carried over from an offer:
 * `{lead_paint_election, lead_paint_days}` when one was recorded. An older
 * offer that selected the lead-paint contingency without an election kept
 * the inspection opportunity, so it carries over as 'inspect'.
 */
export const leadPaintElectionFromOffer = (contingencies) => {
  const recorded =
    contingencies && typeof contingencies === 'object' && !Array.isArray(contingencies) ? contingencies : {};
  const election = getLeadPaintElection(recorded) || (recorded[LEAD_PAINT_KEY] ? LEAD_PAINT_INSPECT : null);
  if (!election) return {};
  const out = { [LEAD_PAINT_ELECTION_KEY]: election, [LEAD_PAINT_KEY]: election === LEAD_PAINT_INSPECT };
  if (election === LEAD_PAINT_INSPECT) {
    const days = parseDeadlineDays(recorded[deadlineKey(LEAD_PAINT_KEY)]);
    if (typeof days === 'number') out[deadlineKey(LEAD_PAINT_KEY)] = days;
  }
  return out;
};

/**
 * Apply a lead-paint choice to form state: inspecting selects the lead-paint
 * contingency (prefilled with 10 days unless a deadline is already there);
 * waiving clears it.
 */
export const applyLeadPaintElection = (contingencies, election) => {
  const next = { ...(contingencies || {}), [LEAD_PAINT_ELECTION_KEY]: election };
  const daysKey = deadlineKey(LEAD_PAINT_KEY);
  if (election === LEAD_PAINT_INSPECT) {
    next[LEAD_PAINT_KEY] = true;
    if (next[daysKey] === undefined || next[daysKey] === null || next[daysKey] === '') {
      next[daysKey] = String(LEAD_PAINT_DEFAULT_DAYS);
    }
  } else {
    next[LEAD_PAINT_KEY] = false;
    delete next[daysKey];
  }
  return next;
};

/**
 * Why the lead-paint election can't be submitted, or null. Only checked when
 * `mode` says it is asked.
 */
export const leadPaintElectionError = (contingencies, mode) => {
  if (!mode) return null;
  const election = getLeadPaintElection(contingencies);
  if (!election) return LEAD_PAINT_COPY.missing;
  if (election === LEAD_PAINT_INSPECT) {
    const days = parseDeadlineDays(contingencies?.[deadlineKey(LEAD_PAINT_KEY)]);
    if (days === undefined) return `Fix the lead-based paint deadline: ${DEADLINE_INVALID_MESSAGE}`;
    if (days === null) return CONTINGENCY_DEADLINE_REQUIRED_MESSAGE;
  }
  return null;
};

/**
 * The summary line for a recorded election ("Lead-based paint: inspection
 * opportunity waived"), or null when the offer has none.
 */
export const getLeadPaintElectionSummary = (contingencies) => {
  const election = getLeadPaintElection(contingencies);
  if (election === LEAD_PAINT_WAIVE) return LEAD_PAINT_COPY.summaryWaive;
  if (election === LEAD_PAINT_INSPECT) {
    const days = parseDeadlineDays(contingencies[deadlineKey(LEAD_PAINT_KEY)]);
    return typeof days === 'number' ? LEAD_PAINT_COPY.summaryInspect(days) : null;
  }
  return null;
};

/**
 * Selected keys to list in a summary: the lead-paint contingency is left out
 * when the election's own line says it.
 */
export const getSummaryContingencyKeys = (contingencies) => {
  const keys = getSelectedContingencyKeys(contingencies);
  return getLeadPaintElectionSummary(contingencies) ? keys.filter((key) => key !== LEAD_PAINT_KEY) : keys;
};

// ── Seller's cure-right election, 7(d) (every WI offer category) ──────────
//
// Legal copy: contingency-notices-copy.v1.md §11 (`WI.offer.cure.*`).
// Backend: `validate_contingency_elections` in
// backend/app/models/offer_contingencies.py. Asked on every Wisconsin offer
// (residential, condo, vacant land) that selects at least one curable
// contingency: one the offer's (d) doesn't exclude. Required there; never
// sent anywhere else. The offer template prints the matching (d) box checked.

export const SELLER_CURE_RIGHT_KEY = 'seller_cure_right';
export const SELLER_CURE_DAYS_KEY = 'seller_cure_days';
export const CURE_RIGHT_CURE = 'cure';
export const CURE_RIGHT_NO_CURE = 'no_cure';
/** The pseudo-key the cure election's field errors are placed under (`unplacedFieldErrors`). */
export const CURE_ELECTION_FIELD = 'seller_cure';

/**
 * Each category's (d) "It does not apply to ..." list on the current offer
 * text: a not-satisfied notice on these ends the offer on delivery, so they
 * never trigger the question. Mirrors `CURE_EXCLUDED_KEYS` in
 * offer_contingencies.py (land includes the §12.2 exclusions).
 */
export const CURE_EXCLUDED_KEYS = {
  [RESIDENTIAL_CATEGORY]: ['financing', 'appraisal', 'insurance', 'sale_of_home'],
  [CONDO_CATEGORY]: ['financing', 'appraisal', 'insurance', 'hoa_review', 'sale_of_home'],
  [VACANT_LAND_CATEGORY]: [
    'financing', 'land_use_approval', 'soil_evaluation', 'water_supply', 'utilities', 'wetlands_floodplain',
  ],
};
/** Keys outside the cure machine: land division has its own path. */
const NEVER_CURABLE_KEYS = ['land_division'];

/** `{excluded_list}` in `WI.offer.cure.help`, by category (copy file §11). */
export const CURE_EXCLUDED_LIST = {
  [RESIDENTIAL_CATEGORY]: 'the financing, appraisal, insurance or sale-of-home contingencies',
  [CONDO_CATEGORY]: 'the financing, appraisal, insurance, association document review or sale-of-home contingencies',
  [VACANT_LAND_CATEGORY]:
    'the financing, zoning and land-use, soil and site evaluation, water supply, utilities or wetlands contingencies',
};

/** Legal's cure-election copy (`WI.offer.cure.*`). */
export const CURE_COPY = {
  title: copyText('WI.offer.cure.title'),
  question: copyText('WI.offer.cure.question'),
  help: (category) =>
    copyText('WI.offer.cure.help', {
      excluded_list: CURE_EXCLUDED_LIST[category] || CURE_EXCLUDED_LIST[RESIDENTIAL_CATEGORY],
    }),
  optionCure: copyText('WI.offer.cure.option.cure'),
  optionNoCure: copyText('WI.offer.cure.option.no_cure'),
  daysLabel: copyText('WI.offer.cure.days_label'),
  daysHelp: copyText('WI.offer.cure.days_help'),
  missing: copyText('WI.offer.cure.missing'),
  summaryCure: (days) => copyText('WI.offer.cure.summary.cure', { cure_days: days }),
  summaryNoCure: copyText('WI.offer.cure.summary.no_cure'),
  counterNote: copyText('WI.offer.cure.counter_note'),
};

/** A typed cure period that isn't whole days in 1–365 (first sentence of `WI.offer.cure.days_help`). */
export const CURE_DAYS_INVALID_MESSAGE = `Enter a whole number of days, from ${MIN_DEADLINE_DAYS} to ${MAX_DEADLINE_DAYS}.`;

/**
 * The Wisconsin offer category whose (d) the election goes into, or null
 * when the offer isn't a Wisconsin offer (the question is never asked).
 * A Wisconsin listing with no category yet uses the residential offer.
 *
 * @param {Object|null|undefined} property - House record (may be null).
 * @param {Object|null} [offer] - The offer being responded to, if any.
 * @returns {string|null}
 */
export const cureElectionCategory = (property, offer = null) => {
  const category = offer ? categoryForOffer(offer, property) : getPaperworkCategory(property);
  const state = stateOf(property, offer);
  if (category === VACANT_LAND_CATEGORY) {
    return state && state !== PAPERWORK_STATE ? null : VACANT_LAND_CATEGORY;
  }
  if (!usesWisconsinHomeOffer(category, state)) return null;
  return category === CONDO_CATEGORY ? CONDO_CATEGORY : RESIDENTIAL_CATEGORY;
};

/** Whether the category's (d) lets the cure window apply to `key`. */
export const isCurableKey = (key, category) => {
  const entry = CONTINGENCY_BY_KEY[key];
  if (!entry || !category || NEVER_CURABLE_KEYS.includes(key)) return false;
  if (!appliesToCategory(entry, category)) return false;
  return !(CURE_EXCLUDED_KEYS[category] || []).includes(key);
};

/** Selected contingencies the cure window may apply to, in catalog order. */
export const curableSelectedKeys = (contingencies, category) =>
  category ? getSelectedContingencyKeys(contingencies).filter((key) => isCurableKey(key, category)) : [];

/**
 * Whether the cure question is asked: a Wisconsin offer (`category` from
 * `cureElectionCategory`) with at least one curable contingency selected.
 */
export const cureElectionAsked = (category, contingencies) =>
  curableSelectedKeys(contingencies, category).length > 0;

/** The election recorded on an offer or form (`cure` | `no_cure`), or null. */
export const getCureElection = (contingencies) => {
  const value =
    contingencies && typeof contingencies === 'object' && !Array.isArray(contingencies)
      ? contingencies[SELLER_CURE_RIGHT_KEY]
      : null;
  return value === CURE_RIGHT_CURE || value === CURE_RIGHT_NO_CURE ? value : null;
};

/** Form state for the election carried over from an offer (`{}` for an older offer without one). */
export const cureElectionFromOffer = (contingencies) => {
  const right = getCureElection(contingencies);
  if (!right) return {};
  const out = { [SELLER_CURE_RIGHT_KEY]: right };
  if (right === CURE_RIGHT_CURE) {
    const days = parseDeadlineDays(contingencies[SELLER_CURE_DAYS_KEY]);
    if (typeof days === 'number') out[SELLER_CURE_DAYS_KEY] = days;
  }
  return out;
};

/** Apply a cure choice to form state: "No cure right" drops the cure period. */
export const applyCureElection = (contingencies, right) => {
  const next = { ...(contingencies || {}), [SELLER_CURE_RIGHT_KEY]: right };
  if (right !== CURE_RIGHT_CURE) delete next[SELLER_CURE_DAYS_KEY];
  return next;
};

/**
 * Why the cure election can't be submitted, or null. Only checked when the
 * question is asked (`cureElectionAsked`).
 */
export const cureElectionError = (contingencies, category) => {
  if (!cureElectionAsked(category, contingencies)) return null;
  const right = getCureElection(contingencies);
  if (!right) return CURE_COPY.missing;
  if (right === CURE_RIGHT_CURE) {
    const days = parseDeadlineDays(contingencies?.[SELLER_CURE_DAYS_KEY]);
    if (days === undefined) return CURE_DAYS_INVALID_MESSAGE;
    if (days === null) return CURE_COPY.missing;
  }
  return null;
};

/** The summary line for a recorded election, or null when the offer has none. */
export const getCureElectionSummary = (contingencies) => {
  const right = getCureElection(contingencies);
  if (right === CURE_RIGHT_NO_CURE) return CURE_COPY.summaryNoCure;
  if (right === CURE_RIGHT_CURE) {
    const days = parseDeadlineDays(contingencies[SELLER_CURE_DAYS_KEY]);
    return typeof days === 'number' ? CURE_COPY.summaryCure(days) : null;
  }
  return null;
};

/** Whether two sets of terms record different cure elections (right or days). */
export const cureElectionChanged = (previous, next) =>
  getCureElectionSummary(previous) !== getCureElectionSummary(next);

// ── Financing loan terms and title periods (every WI offer; QA N13 / D7 / D8) ──
//
// Legal copy: paperwork-copy-and-rules.v1.md §10 (`WI.offer.financing_terms.*`,
// `WI.offer.title_periods.*`; legal review 2026-10-24). Backend:
// `_financing_terms_errors` / `_title_period_errors` in
// backend/app/models/offer_contingencies.py; the offers print them in their
// financing and title sections. Asked on every Wisconsin offer (the category
// from `offerTermsCategory`): both title periods always (on vacant land also
// who gets the commitment and the seller's cure days), and the loan terms
// while the financing contingency is selected (the loan type on residential
// and condo only). Never sent anywhere else. Nothing is prefilled. The loan
// amount isn't a field: the offer prints the offer price minus the down
// payment, rounded down to the whole dollar (`financingLoanAmount`).

export const FINANCING_KEY = 'financing';
export const FINANCING_LOAN_TYPE_KEY = 'financing_loan_type';
export const FINANCING_LOAN_TYPE_OTHER_KEY = 'financing_loan_type_other';
export const FINANCING_TERM_YEARS_KEY = 'financing_term_years';
export const FINANCING_MAX_RATE_KEY = 'financing_max_rate_percent';
export const FINANCING_APPLY_DAYS_KEY = 'financing_apply_days';
export const FINANCING_TERM_KEYS = [
  FINANCING_LOAN_TYPE_KEY,
  FINANCING_LOAN_TYPE_OTHER_KEY,
  FINANCING_TERM_YEARS_KEY,
  FINANCING_MAX_RATE_KEY,
  FINANCING_APPLY_DAYS_KEY,
];
export const LOAN_TYPE_OTHER = 'other';
export const FINANCING_LOAN_TYPES = ['conventional', 'fha', 'va', LOAN_TYPE_OTHER];
/** Loan types whose lenders usually need an appraisal-value addendum (`government_note`). */
export const GOVERNMENT_LOAN_TYPES = ['fha', 'va'];
export const MAX_LOAN_TYPE_OTHER_LENGTH = 60;
export const MIN_LOAN_TERM_YEARS = 1;
export const MAX_LOAN_TERM_YEARS = 40;
export const MAX_FINANCING_RATE_PERCENT = 20;
export const FINANCING_RATE_DECIMALS = 3;
export const TITLE_COMMITMENT_DAYS_KEY = 'title_commitment_days';
export const TITLE_OBJECTION_DAYS_KEY = 'title_objection_days';
export const TITLE_PERIOD_KEYS = [TITLE_COMMITMENT_DAYS_KEY, TITLE_OBJECTION_DAYS_KEY];
export const TITLE_COMMITMENT_PARTY_KEY = 'title_commitment_party';
export const TITLE_CURE_DAYS_KEY = 'title_cure_days';
/** Vacant land only: who gets and pays for the commitment, and the seller's cure days. */
export const LAND_TITLE_KEYS = [TITLE_COMMITMENT_PARTY_KEY, TITLE_CURE_DAYS_KEY];
export const TITLE_COMMITMENT_PARTIES = ['seller', 'buyer'];
/** The pseudo-keys these fields' server errors are placed under (`unplacedFieldErrors`). */
export const FINANCING_TERMS_FIELD = 'financing_terms';
export const TITLE_PERIODS_FIELD = 'title_periods';
/** Categories whose financing clause prints the loan-type boxes. */
const LOAN_TYPE_CATEGORIES = HOME_CATEGORIES;

/** Legal's loan-terms copy (copy file §10, `WI.offer.financing_terms.*`). */
export const FINANCING_TERMS_COPY = {
  title: copyText('WI.offer.financing_terms.title'),
  help: copyText('WI.offer.financing_terms.help'),
  helpSeller: copyText('WI.offer.financing_terms.help_seller'),
  loanTypeLabel: copyText('WI.offer.financing_terms.loan_type.label'),
  loanTypeOption: {
    conventional: copyText('WI.offer.financing_terms.loan_type.option.conventional'),
    fha: copyText('WI.offer.financing_terms.loan_type.option.fha'),
    va: copyText('WI.offer.financing_terms.loan_type.option.va'),
    other: copyText('WI.offer.financing_terms.loan_type.option.other'),
  },
  otherLabel: copyText('WI.offer.financing_terms.loan_type.other_label'),
  otherHelp: copyText('WI.offer.financing_terms.loan_type.other_help'),
  governmentNote: copyText('WI.offer.financing_terms.loan_type.government_note'),
  condoNote: copyText('WI.offer.financing_terms.loan_type.condo_note'),
  amountLabel: copyText('WI.offer.financing_terms.amount.label'),
  amount: (amount) => copyText('WI.offer.financing_terms.amount.line', { amount }),
  amountSeller: (amount) => copyText('WI.offer.financing_terms.amount.line_seller', { amount }),
  termLabel: copyText('WI.offer.financing_terms.term.label'),
  termHelp: copyText('WI.offer.financing_terms.term.help'),
  rateLabel: copyText('WI.offer.financing_terms.rate.label'),
  rateHelp: copyText('WI.offer.financing_terms.rate.help'),
  applyLabel: copyText('WI.offer.financing_terms.apply.label'),
  applyHelp: copyText('WI.offer.financing_terms.apply.help'),
  missing: copyText('WI.offer.financing_terms.missing'),
  otherMissing: copyText('WI.offer.financing_terms.other_missing'),
  termInvalid: copyText('WI.offer.financing_terms.term_invalid'),
  rateInvalid: copyText('WI.offer.financing_terms.rate_invalid'),
  applyInvalid: copyText('WI.offer.financing_terms.apply_invalid'),
  applyAfterDeadline: copyText('WI.offer.financing_terms.apply_after_deadline'),
  fullDownPayment: copyText('WI.offer.financing_terms.full_down_payment'),
  summary: (vars) => copyText('WI.offer.financing_terms.summary', vars),
  summaryLand: (vars) => copyText('WI.offer.financing_terms.summary.land', vars),
  counterNote: copyText('WI.offer.financing_terms.counter_note'),
};

/** `{loan_type}` in `WI.offer.financing_terms.summary`: the template's own words for each box. */
const LOAN_TYPE_SUMMARY = { conventional: 'conventional', fha: 'FHA', va: 'VA' };

/** Legal's title copy (copy file §10, `WI.offer.title_periods.*`). */
export const TITLE_PERIODS_COPY = {
  title: copyText('WI.offer.title_periods.title'),
  help: copyText('WI.offer.title_periods.help'),
  helpSeller: copyText('WI.offer.title_periods.help_seller'),
  deliveryLabel: copyText('WI.offer.title_periods.delivery.label'),
  deliveryHelp: copyText('WI.offer.title_periods.delivery.help'),
  objectionLabel: copyText('WI.offer.title_periods.objection.label'),
  objectionLabelSeller: copyText('WI.offer.title_periods.objection.label_seller'),
  objectionHelp: copyText('WI.offer.title_periods.objection.help'),
  objectionHelpSeller: copyText('WI.offer.title_periods.objection.help_seller'),
  missing: copyText('WI.offer.title_periods.missing'),
  invalid: copyText('WI.offer.title_periods.invalid'),
  summary: (vars) => copyText('WI.offer.title_periods.summary', vars),
  counterNote: copyText('WI.offer.title_periods.counter_note'),
  titleReviewTooEarly: copyText('WI.offer.title_periods.title_review_too_early'),
  warningDeliveryAfterClosing: (closingDate) =>
    copyText('WI.offer.title_periods.warning.delivery_after_closing', { closing_date: closingDate }),
  warningObjectionAfterClosing: (closingDate) =>
    copyText('WI.offer.title_periods.warning.objection_after_closing', { closing_date: closingDate }),
  land: {
    help: copyText('WI.offer.title_periods.land.help'),
    helpSeller: copyText('WI.offer.title_periods.land.help_seller'),
    partyLabel: copyText('WI.offer.title_periods.land.party.label'),
    partyOption: {
      seller: copyText('WI.offer.title_periods.land.party.option.seller'),
      buyer: copyText('WI.offer.title_periods.land.party.option.buyer'),
    },
    deliveryLabel: copyText('WI.offer.title_periods.land.delivery.label'),
    objectionHelp: copyText('WI.offer.title_periods.land.objection.help'),
    cureDaysLabel: copyText('WI.offer.title_periods.land.cure_days.label'),
    cureDaysHelp: copyText('WI.offer.title_periods.land.cure_days.help'),
    missing: copyText('WI.offer.title_periods.land.missing'),
    summary: (vars) => copyText('WI.offer.title_periods.summary.land', vars),
  },
};

/**
 * The Wisconsin offer category whose financing and title sections print these
 * terms, or null when the offer isn't a Wisconsin offer (they are never asked).
 * The same offers take the cure election.
 *
 * @param {Object|null|undefined} property - House record (may be null).
 * @param {Object|null} [offer] - The offer being responded to, if any.
 * @returns {string|null}
 */
export const offerTermsCategory = (property, offer = null) => cureElectionCategory(property, offer);

/** Whether the category's financing clause has loan-type boxes (not vacant land). */
export const loanTypeAsked = (category) => LOAN_TYPE_CATEGORIES.includes(category);

/** Whether the category's title section takes the party and cure days (vacant land). */
export const landTitleAsked = (category) => category === VACANT_LAND_CATEGORY;

/** Whether the loan terms are asked: a Wisconsin offer with the financing contingency selected. */
export const financingTermsAsked = (category, contingencies) =>
  Boolean(category) && Boolean(contingencies?.[FINANCING_KEY]);

/**
 * Parse a whole number in [min, max]: the number, `null` when blank, or
 * `undefined` when it isn't one.
 */
export const parseWholeNumberIn = (raw, min, max) => {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (text === '') return null;
  if (!WHOLE_NUMBER.test(text)) return undefined;
  const value = Number(text);
  return value >= min && value <= max ? value : undefined;
};

const RATE_PATTERN = new RegExp(`^(\\d+(\\.\\d{0,${FINANCING_RATE_DECIMALS}})?|\\.\\d{1,${FINANCING_RATE_DECIMALS}})$`);

/**
 * Parse the maximum interest rate: a percentage above 0 and at most 20, to 3
 * decimals. The number, `null` when blank, or `undefined` when invalid.
 */
export const parseFinancingRate = (raw) => {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (text === '') return null;
  if (!RATE_PATTERN.test(text)) return undefined;
  const value = Number(text);
  return value > 0 && value <= MAX_FINANCING_RATE_PERCENT ? value : undefined;
};

/**
 * The down payment as the offer stores it: two decimals, rounding half up
 * (`stored_down_payment`), so 3.333 is 3.33 and 99.995 is 100. Null when
 * blank or not a number.
 */
export const storedDownPayment = (downPaymentPercent) => {
  if (downPaymentPercent === '' || downPaymentPercent === null || downPaymentPercent === undefined) return null;
  const down = Number(downPaymentPercent);
  if (!Number.isFinite(down)) return null;
  // Exponent arithmetic rounds the decimal value (3.335 -> 3.34), not its binary approximation.
  return Number(`${Math.round(Number(`${down}e2`))}e-2`);
};

/**
 * The loan the financing clause names: the offer price minus the down
 * payment (as stored), rounded down to the whole dollar; null when it can't
 * be computed or isn't at least $1 (a 100% down payment). Mirrors
 * `financing_loan_amount`.
 */
export const financingLoanAmount = (offerPrice, downPaymentPercent) => {
  if (offerPrice === '' || offerPrice === null || offerPrice === undefined) return null;
  const price = Number(offerPrice);
  const down = storedDownPayment(downPaymentPercent);
  if (!Number.isFinite(price) || down === null) return null;
  // The epsilon absorbs binary rounding (e.g. 96.7 * 279000), never a real cent.
  const dollars = Math.floor((price * (100 - down)) / 100 + 1e-6);
  return dollars > 0 ? dollars : null;
};

/** "$228,000.00": the amount as the offer prints it. */
export const formatLoanAmount = (amount) =>
  `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The stored loan type, or null. */
export const getLoanType = (contingencies) => {
  const value = contingencies?.[FINANCING_LOAN_TYPE_KEY];
  return FINANCING_LOAN_TYPES.includes(value) ? value : null;
};

/** The stored party who gets the land title commitment, or null. */
export const getTitleCommitmentParty = (contingencies) => {
  const value = contingencies?.[TITLE_COMMITMENT_PARTY_KEY];
  return TITLE_COMMITMENT_PARTIES.includes(value) ? value : null;
};

const recordedOf = (contingencies) =>
  contingencies && typeof contingencies === 'object' && !Array.isArray(contingencies) ? contingencies : {};

/** Form state for the loan terms carried over from an offer (`{}` for an older offer without them). */
export const financingTermsFromOffer = (contingencies) => {
  const recorded = recordedOf(contingencies);
  const out = {};
  const type = getLoanType(recorded);
  if (type) out[FINANCING_LOAN_TYPE_KEY] = type;
  const other = typeof recorded[FINANCING_LOAN_TYPE_OTHER_KEY] === 'string'
    ? recorded[FINANCING_LOAN_TYPE_OTHER_KEY].trim() : '';
  if (type === LOAN_TYPE_OTHER && other) out[FINANCING_LOAN_TYPE_OTHER_KEY] = other;
  const term = parseWholeNumberIn(recorded[FINANCING_TERM_YEARS_KEY], MIN_LOAN_TERM_YEARS, MAX_LOAN_TERM_YEARS);
  if (typeof term === 'number') out[FINANCING_TERM_YEARS_KEY] = term;
  const rate = parseFinancingRate(recorded[FINANCING_MAX_RATE_KEY]);
  if (typeof rate === 'number') out[FINANCING_MAX_RATE_KEY] = rate;
  const apply = parseDeadlineDays(recorded[FINANCING_APPLY_DAYS_KEY]);
  if (typeof apply === 'number') out[FINANCING_APPLY_DAYS_KEY] = apply;
  return out;
};

/** Form state for the title terms carried over from an offer (`{}` for an older offer without them). */
export const titlePeriodsFromOffer = (contingencies) => {
  const recorded = recordedOf(contingencies);
  const out = {};
  [...TITLE_PERIOD_KEYS, TITLE_CURE_DAYS_KEY].forEach((key) => {
    const days = parseDeadlineDays(recorded[key]);
    if (typeof days === 'number') out[key] = days;
  });
  const party = getTitleCommitmentParty(recorded);
  if (party) out[TITLE_COMMITMENT_PARTY_KEY] = party;
  return out;
};

/** Switching the financing contingency off drops its loan terms (and its deadline). */
export const withoutFinancingTerms = (contingencies) => {
  const next = { ...(contingencies || {}) };
  FINANCING_TERM_KEYS.forEach((key) => delete next[key]);
  return next;
};

/** A loan type choice: anything but "other" drops the description. */
export const applyLoanType = (contingencies, type) => {
  const next = { ...(contingencies || {}), [FINANCING_LOAN_TYPE_KEY]: type };
  if (type !== LOAN_TYPE_OTHER) delete next[FINANCING_LOAN_TYPE_OTHER_KEY];
  return next;
};

const fullDownPayment = (downPaymentPercent) => {
  const down = storedDownPayment(downPaymentPercent);
  return down !== null && down >= 100;
};

/**
 * Each loan-term field's problem, `{field: message}` (empty when they can be
 * submitted). Only checked when the terms are asked (`financingTermsAsked`).
 * A 100% down payment is reported under `financing`.
 *
 * @param {Object} contingencies - Form state.
 * @param {string|null} category - From `offerTermsCategory`.
 * @param {number|string|null} [downPaymentPercent] - The offer's down payment.
 * @returns {Object<string, string>}
 */
export const financingTermsFieldErrors = (contingencies, category, downPaymentPercent = null) => {
  const out = {};
  if (!financingTermsAsked(category, contingencies)) return out;
  if (loanTypeAsked(category)) {
    const type = getLoanType(contingencies);
    if (!type) out[FINANCING_LOAN_TYPE_KEY] = FINANCING_TERMS_COPY.missing;
    else if (type === LOAN_TYPE_OTHER) {
      const raw = contingencies?.[FINANCING_LOAN_TYPE_OTHER_KEY];
      const text = typeof raw === 'string' ? raw.trim() : '';
      if (!text || text.length > MAX_LOAN_TYPE_OTHER_LENGTH) {
        out[FINANCING_LOAN_TYPE_OTHER_KEY] = FINANCING_TERMS_COPY.otherMissing;
      }
    }
  }
  const term = parseWholeNumberIn(
    contingencies?.[FINANCING_TERM_YEARS_KEY], MIN_LOAN_TERM_YEARS, MAX_LOAN_TERM_YEARS,
  );
  if (term === null) out[FINANCING_TERM_YEARS_KEY] = FINANCING_TERMS_COPY.missing;
  else if (term === undefined) out[FINANCING_TERM_YEARS_KEY] = FINANCING_TERMS_COPY.termInvalid;
  const rate = parseFinancingRate(contingencies?.[FINANCING_MAX_RATE_KEY]);
  if (rate === null) out[FINANCING_MAX_RATE_KEY] = FINANCING_TERMS_COPY.missing;
  else if (rate === undefined) out[FINANCING_MAX_RATE_KEY] = FINANCING_TERMS_COPY.rateInvalid;
  const apply = parseDeadlineDays(contingencies?.[FINANCING_APPLY_DAYS_KEY]);
  const deadline = parseDeadlineDays(contingencies?.[deadlineKey(FINANCING_KEY)]);
  if (apply === null) out[FINANCING_APPLY_DAYS_KEY] = FINANCING_TERMS_COPY.missing;
  else if (apply === undefined) out[FINANCING_APPLY_DAYS_KEY] = FINANCING_TERMS_COPY.applyInvalid;
  else if (typeof deadline === 'number' && apply >= deadline) {
    // The time to apply must end before the financing deadline (legal review 2026-10-24).
    out[FINANCING_APPLY_DAYS_KEY] = FINANCING_TERMS_COPY.applyAfterDeadline;
  }
  if (fullDownPayment(downPaymentPercent)) out[FINANCING_KEY] = FINANCING_TERMS_COPY.fullDownPayment;
  return out;
};

/** Why the loan terms can't be submitted (the first problem), or null. */
export const financingTermsError = (contingencies, category, downPaymentPercent = null) =>
  Object.values(financingTermsFieldErrors(contingencies, category, downPaymentPercent))[0] || null;

/**
 * Each title field's problem, `{field: message}`; only on a Wisconsin offer.
 * On vacant land the party and cure days too. With the title review
 * contingency selected (residential, condo), its deadline must be later than
 * the commitment period (`title_days`).
 */
export const titlePeriodsFieldErrors = (contingencies, category) => {
  const out = {};
  if (!category) return out;
  TITLE_PERIOD_KEYS.forEach((key) => {
    const days = parseDeadlineDays(contingencies?.[key]);
    if (days === null) out[key] = TITLE_PERIODS_COPY.missing;
    else if (days === undefined) out[key] = TITLE_PERIODS_COPY.invalid;
  });
  if (landTitleAsked(category)) {
    if (!getTitleCommitmentParty(contingencies)) out[TITLE_COMMITMENT_PARTY_KEY] = TITLE_PERIODS_COPY.land.missing;
    const cure = parseDeadlineDays(contingencies?.[TITLE_CURE_DAYS_KEY]);
    if (cure === null) out[TITLE_CURE_DAYS_KEY] = TITLE_PERIODS_COPY.land.missing;
    else if (cure === undefined) out[TITLE_CURE_DAYS_KEY] = TITLE_PERIODS_COPY.invalid;
  } else if (contingencies?.title) {
    const review = parseDeadlineDays(contingencies?.[deadlineKey('title')]);
    const delivery = parseDeadlineDays(contingencies?.[TITLE_COMMITMENT_DAYS_KEY]);
    if (typeof review === 'number' && typeof delivery === 'number' && review <= delivery) {
      out[deadlineKey('title')] = TITLE_PERIODS_COPY.titleReviewTooEarly;
    }
  }
  return out;
};

/** Why the title terms can't be submitted (the first problem), or null. */
export const titlePeriodsError = (contingencies, category) =>
  Object.values(titlePeriodsFieldErrors(contingencies, category))[0] || null;

/** The loan type as the summary names it ("conventional", "FHA", or the "other" description). */
const loanTypeSummaryLabel = (contingencies) => {
  const type = getLoanType(contingencies);
  if (type === LOAN_TYPE_OTHER) {
    const raw = contingencies?.[FINANCING_LOAN_TYPE_OTHER_KEY];
    return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
  }
  return type ? LOAN_TYPE_SUMMARY[type] : null;
};

/**
 * The summary line for recorded loan terms (`WI.offer.financing_terms.summary`,
 * or `.summary.land` without a loan type), or null when the offer has none (no
 * financing contingency, or an older offer without them).
 *
 * @param {Object|null|undefined} contingencies - Stored value or form state.
 * @param {number|null} amount - From `financingLoanAmount`.
 * @returns {string|null}
 */
export const getFinancingTermsSummary = (contingencies, amount) => {
  if (!contingencies?.[FINANCING_KEY]) return null;
  const terms = financingTermsFromOffer(contingencies);
  const term = terms[FINANCING_TERM_YEARS_KEY];
  const rate = terms[FINANCING_MAX_RATE_KEY];
  const apply = terms[FINANCING_APPLY_DAYS_KEY];
  if (term === undefined || rate === undefined || apply === undefined || typeof amount !== 'number') return null;
  const vars = { amount: formatLoanAmount(amount), term_years: term, max_rate: rate, apply_days: apply };
  const loanType = loanTypeSummaryLabel(contingencies);
  return loanType
    ? FINANCING_TERMS_COPY.summary({ ...vars, loan_type: loanType })
    : FINANCING_TERMS_COPY.summaryLand(vars);
};

/**
 * The summary line for recorded title terms (`WI.offer.title_periods.summary`,
 * or `.summary.land` with the party and cure days), or null when the offer has
 * none.
 */
export const getTitlePeriodsSummary = (contingencies) => {
  const periods = titlePeriodsFromOffer(contingencies);
  const delivery = periods[TITLE_COMMITMENT_DAYS_KEY];
  const objection = periods[TITLE_OBJECTION_DAYS_KEY];
  if (delivery === undefined || objection === undefined) return null;
  const party = periods[TITLE_COMMITMENT_PARTY_KEY];
  const cure = periods[TITLE_CURE_DAYS_KEY];
  if (party && cure !== undefined) {
    return TITLE_PERIODS_COPY.land.summary({
      party, delivery_days: delivery, objection_days: objection, cure_days: cure,
    });
  }
  return TITLE_PERIODS_COPY.summary({ delivery_days: delivery, objection_days: objection });
};

/** Whether two sets of terms record different loan terms. */
export const financingTermsChanged = (previous, next) =>
  JSON.stringify(financingTermsFromOffer(previous)) !== JSON.stringify(financingTermsFromOffer(next))
  || Boolean(previous?.[FINANCING_KEY]) !== Boolean(next?.[FINANCING_KEY]);

/** Whether two sets of terms record different title terms. */
export const titlePeriodsChanged = (previous, next) =>
  JSON.stringify(titlePeriodsFromOffer(previous)) !== JSON.stringify(titlePeriodsFromOffer(next));

/** Whether a catalog entry may be offered on a listing of this category. */
const appliesToCategory = (entry, category) => {
  if (entry.categories) return entry.categories.includes(category);
  if (entry.excludeCategories) return !entry.excludeCategories.includes(category);
  return true;
};

/**
 * The entry as shown on a listing of this category: the land wording on land.
 * `deadline` says a `<key>_days` field is offered (every available entry),
 * `deadlineRequired` that it must be filled in when selected (vacant land,
 * and Wisconsin residential and condo offers).
 * `unavailable` marks an entry the listing's offer can't print, shown only
 * because an existing offer already selected it (see
 * `getContingencyOptionsForOffer`): the server refuses it, so it must be
 * removed.
 */
const presentFor = (entry, category, requireDeadlines = category === VACANT_LAND_CATEGORY) => {
  const isLand = category === VACANT_LAND_CATEGORY;
  const unavailable = !appliesToCategory(entry, category);
  const { land: landVariant, ...base } = entry;
  return {
    ...base,
    ...(isLand && landVariant ? landVariant : {}),
    deadline: !unavailable,
    deadlineRequired: requireDeadlines && !unavailable,
    unavailable,
  };
};

/**
 * Contingencies offered for a given property, in catalog order.
 *
 * On a vacant land listing the land-only contingencies are added and the
 * home-only ones (home inspection, radon, lead paint) removed. Where the
 * lead-paint election is asked (`leadPaintElectionMode`), it replaces the
 * lead-paint card.
 *
 * @param {Object|null|undefined} property - House record; when omitted only the
 *   universally applicable contingencies are returned.
 * @returns {Array<Object>} Catalog entries (with `deadline` set on land).
 */
export const getContingencyOptions = (property) => {
  const facts = derivePropertyFacts(property);
  const category = getPaperworkCategory(property);
  const requireDeadlines = deadlinesRequiredFor(category, stateOf(property));
  const election = Boolean(leadPaintElectionMode(property));
  return CONTINGENCY_CATALOG.filter(
    (entry) =>
      appliesToCategory(entry, category) &&
      (!entry.appliesTo || entry.appliesTo(facts)) &&
      !(election && entry.key === LEAD_PAINT_KEY)
  ).map((entry) => presentFor(entry, category, requireDeadlines));
};

/**
 * The category to use when responding to an offer: the listing's, else the
 * offer's own `paperwork_category`. When neither is known, an offer carrying a
 * land-only election is a land offer: the server clears those keys on every
 * other listing.
 */
const categoryForOffer = (offer, property) => {
  const known = getPaperworkCategory(property) || offer?.paperwork_category || null;
  if (known) return known;
  const recorded = (offer && typeof offer.contingencies === 'object' && offer.contingencies) || {};
  return LAND_ONLY_CONTINGENCY_KEYS.some((key) => recorded[key]) ? VACANT_LAND_CATEGORY : null;
};

/**
 * Contingencies to show when responding to an existing offer.
 *
 * A counter must never silently drop a term the other side already agreed to,
 * so anything recorded on the offer is included even when the listing data no
 * longer says it applies (or was never loaded). Entries limited by category
 * (land only, or hidden on land) are included off-category only when actually
 * elected: stored offers carry every catalog key, mostly `false`.
 *
 * @param {Object|null|undefined} offer - Offer with a `contingencies` dict.
 * @param {Object|null|undefined} property - House record, when available.
 * @returns {Array<Object>} Catalog entries.
 */
export const getContingencyOptionsForOffer = (offer, property) => {
  const facts = derivePropertyFacts(property);
  const category = categoryForOffer(offer, property);
  const requireDeadlines = deadlinesRequiredFor(category, stateOf(property, offer));
  const election = Boolean(leadPaintElectionMode(property, offer || {}));
  const recorded = (offer && typeof offer.contingencies === 'object' && offer.contingencies) || {};
  return CONTINGENCY_CATALOG.filter((entry) => {
    if (election && entry.key === LEAD_PAINT_KEY) return false;
    if (!appliesToCategory(entry, category)) return Boolean(recorded[entry.key]);
    // Only an elected key counts: stored offers carry every catalog key, mostly
    // `false`, and offering those would show options the listing rules out.
    return !entry.appliesTo || entry.appliesTo(facts) || Boolean(recorded[entry.key]);
  }).map((entry) => presentFor(entry, category, requireDeadlines));
};

// ── Deadlines (`<key>_days`) ─────────────────────────────────────────────

const WHOLE_NUMBER = /^\d+$/;

/**
 * Parse a deadline: a whole number of days in 1–365, `null` when blank (no
 * deadline), or `undefined` when it isn't a valid deadline.
 *
 * @param {string|number|null|undefined} raw
 * @returns {number|null|undefined}
 */
export const parseDeadlineDays = (raw) => {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (text === '') return null;
  if (!WHOLE_NUMBER.test(text)) return undefined;
  const days = Number(text);
  return days >= MIN_DEADLINE_DAYS && days <= MAX_DEADLINE_DAYS ? days : undefined;
};

/** `WI.offer.contingency_deadline.required`: a selected contingency has no deadline. */
export const CONTINGENCY_DEADLINE_REQUIRED_MESSAGE = copyText('WI.offer.contingency_deadline.required');

/**
 * A typed deadline that isn't whole days in range, where deadlines are
 * required (every Wisconsin offer: legal L4 §9.7, §9.8b item 2). Legal's
 * `WI.offer.contingency_deadline.required` wording; blank is not offered as a
 * way out.
 */
export const DEADLINE_INVALID_MESSAGE = CONTINGENCY_DEADLINE_REQUIRED_MESSAGE;

/** The range alone, for an optional deadline typed out of range (offers outside Wisconsin). */
export const DEADLINE_RANGE_MESSAGE = `Enter a whole number of days from ${MIN_DEADLINE_DAYS} to ${MAX_DEADLINE_DAYS}.`;

/** The same, next to an optional deadline field (offers outside Wisconsin). */
export const DEADLINE_INVALID_OPTIONAL_MESSAGE = `Enter a whole number of days from ${MIN_DEADLINE_DAYS} to ${MAX_DEADLINE_DAYS}, or leave it blank.`;

/** A selected land contingency without a deadline (L4 §9.7 item 1). */
export const DEADLINE_REQUIRED_MESSAGE = 'Enter the number of days after acceptance.';

/** `WI.offer.contingency_deadline.note`: shown with the contingencies on WI home and condo offers. */
export const CONTINGENCY_DEADLINE_NOTE = copyText('WI.offer.contingency_deadline.note');

/** `soil_evaluation` → `soil_evaluation_days`. */
export const deadlineKey = (key) => `${key}${DEADLINE_SUFFIX}`;

/**
 * The valid deadlines recorded on an offer, `{<key>_days: number}`.
 *
 * @param {Object|null|undefined} contingencies - Stored value.
 * @returns {Object<string, number>}
 */
export const normalizeContingencyDeadlines = (contingencies) => {
  const out = {};
  if (!contingencies || typeof contingencies !== 'object' || Array.isArray(contingencies)) return out;
  DEADLINE_CONTINGENCY_KEYS.forEach((key) => {
    const days = parseDeadlineDays(contingencies[deadlineKey(key)]);
    if (typeof days === 'number') out[deadlineKey(key)] = days;
  });
  return out;
};

/**
 * Contingencies that are switched on but whose deadline was typed and isn't
 * 1–365 whole days.
 *
 * @param {Object} contingencies - Form state (flags plus `<key>_days` text).
 * @returns {string[]} Contingency keys.
 */
export const invalidDeadlineKeys = (contingencies) =>
  DEADLINE_CONTINGENCY_KEYS.filter(
    (key) => contingencies?.[key] && parseDeadlineDays(contingencies[deadlineKey(key)]) === undefined
  );

/**
 * Selected contingencies that must carry a deadline but have none.
 *
 * On the vacant land offer, and on Wisconsin residential and condo offers,
 * every selected contingency that takes a deadline must show one (template
 * 7(a); legal review §9.7 item 1): a blank deadline makes an ambiguous
 * contract, and the server refuses it with a 422. The
 * `options` are the entries the form renders (`getContingencyOptions` /
 * `getContingencyOptionsForOffer`), whose `deadline` flag is true only where
 * a deadline field is shown, so this is required exactly where it is asked.
 *
 * @param {Object} contingencies - Form state (flags plus `<key>_days` text).
 * @param {Array<Object>} options - Catalog entries as rendered.
 * @returns {string[]} Contingency keys, in `options` order.
 */
export const missingDeadlineKeys = (contingencies, options = []) =>
  options
    .filter(
      ({ key, deadlineRequired }) =>
        deadlineRequired && contingencies?.[key] && parseDeadlineDays(contingencies[deadlineKey(key)]) === null
    )
    .map(({ key }) => key);

/**
 * Selected contingencies the listing's offer can't print (`unavailable`
 * entries, carried over from an existing offer). The server refuses them.
 *
 * @param {Object} contingencies - Form state.
 * @param {Array<Object>} options - Catalog entries as rendered.
 * @returns {string[]} Contingency keys.
 */
export const unavailableSelectedKeys = (contingencies, options = []) =>
  options.filter(({ key, unavailable }) => unavailable && contingencies?.[key]).map(({ key }) => key);

/**
 * Counter forms (UI chrome, no legal statement): shown while the contingencies
 * are untouched but wouldn't pass today's rules (e.g. an older offer with a
 * blank deadline). An untouched counter omits them and the server carries the
 * stored terms forward; changing any of them applies the rules.
 */
export const CONTINGENCIES_UNCHANGED_NOTE =
  'Unless you change them, your counter keeps the contingencies exactly as they are on the current offer.';

/** Shown next to a selected contingency the listing's offer can't include. */
export const CONTINGENCY_UNAVAILABLE_MESSAGE = "This listing's offer can't include this contingency. Remove it.";

/**
 * Why the contingency elections can't be submitted yet, or null when they
 * can: a contingency the offer can't print, then an out-of-range deadline,
 * then a missing required one.
 *
 * @param {Object} contingencies - Form state.
 * @param {Array<Object>} options - Catalog entries as rendered.
 * @returns {string|null}
 */
export const contingencyElectionError = (contingencies, options = []) => {
  const labelOf = (key) => options.find((o) => o.key === key)?.label || getContingencyLabel(key);
  const unavailable = unavailableSelectedKeys(contingencies, options);
  if (unavailable.length > 0) {
    return `Remove contingencies this listing's offer can't include: ${unavailable.map(labelOf).join(', ')}.`;
  }
  const invalid = invalidDeadlineKeys(contingencies);
  if (invalid.length > 0) {
    // Legal's required-deadline wording where deadlines are required (WI);
    // the range alone where they are optional.
    const required = invalid.some((key) => options.find((o) => o.key === key)?.deadlineRequired);
    return `Fix the contingency deadline: ${required ? DEADLINE_INVALID_MESSAGE : DEADLINE_RANGE_MESSAGE}`;
  }
  const missing = missingDeadlineKeys(contingencies, options);
  if (missing.length > 0) {
    // WI.offer.contingency_deadline.required, then which ones (UI chrome).
    return `${CONTINGENCY_DEADLINE_REQUIRED_MESSAGE} Missing: ${missing.map(labelOf).join(', ')}.`;
  }
  return null;
};

/**
 * `contingencyElectionError`, then the lead-paint election when it is asked
 * (`leadPaintMode` from `leadPaintElectionMode`).
 *
 * @param {Object} contingencies - Form state.
 * @param {Array<Object>} options - Catalog entries as rendered.
 * Then the cure election, where it is asked (`cureCategory` from
 * `cureElectionCategory`, and a curable contingency selected). Then, on a
 * Wisconsin offer (`terms.category` from `offerTermsCategory`), the
 * financing loan terms and the title periods.
 *
 * @param {string|null} [leadPaintMode]
 * @param {string|null} [cureCategory]
 * @param {{category?: string|null, downPaymentPercent?: number|string|null}} [terms]
 * @returns {string|null}
 */
export const offerElectionError = (
  contingencies,
  options = [],
  leadPaintMode = null,
  cureCategory = null,
  { category: termsCategory = null, downPaymentPercent = null } = {},
) =>
  contingencyElectionError(contingencies, options)
  || leadPaintElectionError(contingencies, leadPaintMode)
  || cureElectionError(contingencies, cureCategory)
  || financingTermsError(contingencies, termsCategory, downPaymentPercent)
  || titlePeriodsError(contingencies, termsCategory);

/**
 * The contingencies payload to send: the flags as they are, and a deadline
 * (as a number) only for a contingency that is on and has one.
 *
 * `explicitKeys` are always sent as booleans, so "none selected" is an
 * explicit election rather than an omission. The server refuses an offer
 * whose contingencies are omitted.
 *
 * `leadPaintMode` (from `leadPaintElectionMode`): when set, the election is
 * sent (`lead_paint_election`, with `lead_paint` and, for 'inspect',
 * `lead_paint_days`); when not, `lead_paint_election` is never sent.
 *
 * `cureCategory` (from `cureElectionCategory`): when set and a curable
 * contingency is selected, the cure election is sent (`seller_cure_right`,
 * with `seller_cure_days` for 'cure'); otherwise neither is ever sent.
 *
 * `termsCategory` (from `offerTermsCategory`): when set, the title periods
 * are sent (as numbers; on vacant land with the party and cure days), and so
 * are the financing loan terms while the financing contingency is on (the
 * loan type on residential and condo, its description only with "other");
 * otherwise none of them is ever sent.
 *
 * @param {Object} contingencies - Form state.
 * @param {{explicitKeys?: string[], leadPaintMode?: string|null, cureCategory?: string|null,
 *   termsCategory?: string|null}} [options]
 * @returns {Object}
 */
export const serializeContingencies = (
  contingencies,
  { explicitKeys = [], leadPaintMode = null, cureCategory = null, termsCategory = null } = {},
) => {
  const out = {};
  explicitKeys.forEach((key) => {
    out[key] = !!contingencies?.[key];
  });
  const termKeys = [...FINANCING_TERM_KEYS, ...TITLE_PERIOD_KEYS, ...LAND_TITLE_KEYS];
  Object.entries(contingencies || {}).forEach(([key, value]) => {
    if (
      !key.endsWith(DEADLINE_SUFFIX) && key !== LEAD_PAINT_ELECTION_KEY && key !== SELLER_CURE_RIGHT_KEY
      && !termKeys.includes(key)
    ) {
      out[key] = value;
    }
  });
  if (termsCategory) {
    TITLE_PERIOD_KEYS.forEach((key) => {
      const days = parseDeadlineDays(contingencies?.[key]);
      if (typeof days === 'number') out[key] = days;
    });
    if (landTitleAsked(termsCategory)) {
      const party = getTitleCommitmentParty(contingencies);
      if (party) out[TITLE_COMMITMENT_PARTY_KEY] = party;
      const cure = parseDeadlineDays(contingencies?.[TITLE_CURE_DAYS_KEY]);
      if (typeof cure === 'number') out[TITLE_CURE_DAYS_KEY] = cure;
    }
    if (financingTermsAsked(termsCategory, contingencies)) {
      const type = getLoanType(contingencies);
      if (loanTypeAsked(termsCategory) && type) {
        out[FINANCING_LOAN_TYPE_KEY] = type;
        const other = contingencies?.[FINANCING_LOAN_TYPE_OTHER_KEY];
        if (type === LOAN_TYPE_OTHER && typeof other === 'string' && other.trim()) {
          out[FINANCING_LOAN_TYPE_OTHER_KEY] = other.trim();
        }
      }
      const term = parseWholeNumberIn(
        contingencies?.[FINANCING_TERM_YEARS_KEY], MIN_LOAN_TERM_YEARS, MAX_LOAN_TERM_YEARS,
      );
      if (typeof term === 'number') out[FINANCING_TERM_YEARS_KEY] = term;
      const rate = parseFinancingRate(contingencies?.[FINANCING_MAX_RATE_KEY]);
      if (typeof rate === 'number') out[FINANCING_MAX_RATE_KEY] = rate;
      const apply = parseDeadlineDays(contingencies?.[FINANCING_APPLY_DAYS_KEY]);
      if (typeof apply === 'number') out[FINANCING_APPLY_DAYS_KEY] = apply;
    }
  }
  DEADLINE_CONTINGENCY_KEYS.forEach((key) => {
    const days = parseDeadlineDays(contingencies?.[deadlineKey(key)]);
    if (contingencies?.[key] && typeof days === 'number') out[deadlineKey(key)] = days;
  });
  const election = getLeadPaintElection(contingencies);
  if (leadPaintMode && election) {
    out[LEAD_PAINT_ELECTION_KEY] = election;
    out[LEAD_PAINT_KEY] = election === LEAD_PAINT_INSPECT;
    const days = parseDeadlineDays(contingencies?.[deadlineKey(LEAD_PAINT_KEY)]);
    if (election === LEAD_PAINT_INSPECT && typeof days === 'number') out[deadlineKey(LEAD_PAINT_KEY)] = days;
    else delete out[deadlineKey(LEAD_PAINT_KEY)];
  }
  const cureRight = getCureElection(contingencies);
  if (cureRight && cureElectionAsked(cureCategory, contingencies)) {
    out[SELLER_CURE_RIGHT_KEY] = cureRight;
    const cureDays = parseDeadlineDays(contingencies?.[SELLER_CURE_DAYS_KEY]);
    if (cureRight === CURE_RIGHT_CURE && typeof cureDays === 'number') out[SELLER_CURE_DAYS_KEY] = cureDays;
  }
  return out;
};

/**
 * A selected contingency's label, with its deadline when one was set:
 * "Soil & septic site evaluation (15 days)".
 *
 * @param {string} key - Contingency key.
 * @param {Object|null|undefined} contingencies - Stored value.
 * @returns {string}
 */
export const getContingencyLabelWithDeadline = (key, contingencies) => {
  const days =
    contingencies && typeof contingencies === 'object' && !Array.isArray(contingencies)
      ? parseDeadlineDays(contingencies[deadlineKey(key)])
      : null;
  const label = getContingencyLabel(key);
  return typeof days === 'number' ? `${label} (${days} ${days === 1 ? 'day' : 'days'})` : label;
};

/**
 * Normalize an offer's stored contingencies into a boolean map, dropping the
 * free-text `other` note and any key we no longer recognize.
 *
 * @param {Object|Array|null|undefined} contingencies - Stored value. Legacy
 *   rows may hold an array of selected keys instead of a dict.
 * @returns {Object<string, boolean>} Map of catalog key to selected state.
 */
export const normalizeContingencies = (contingencies) => {
  const out = {};
  if (Array.isArray(contingencies)) {
    contingencies.forEach((key) => {
      if (CONTINGENCY_BY_KEY[key]) out[key] = true;
    });
    return out;
  }
  if (!contingencies || typeof contingencies !== 'object') return out;
  CONTINGENCY_KEYS.forEach((key) => {
    if (hasKey(contingencies, key)) out[key] = !!contingencies[key];
  });
  return out;
};

/**
 * Catalog keys selected on an offer, in catalog order.
 *
 * @param {Object|Array|null|undefined} contingencies - Stored value.
 * @returns {string[]} Selected keys.
 */
export const getSelectedContingencyKeys = (contingencies) => {
  const normalized = normalizeContingencies(contingencies);
  return CONTINGENCY_KEYS.filter((key) => normalized[key]);
};

/**
 * Human label for a contingency key, falling back to a title-cased key so
 * unknown/legacy values still render something sensible.
 *
 * @param {string} key - Contingency key.
 * @returns {string} Display label.
 */
export const getContingencyLabel = (key) => {
  if (CONTINGENCY_BY_KEY[key]) return CONTINGENCY_BY_KEY[key].label;
  if (typeof key !== 'string' || !key) return '';
  return key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
};

/**
 * Buyer-facing description for a contingency key.
 *
 * @param {string} key - Contingency key.
 * @returns {string} Description, or an empty string when unknown.
 */
export const getContingencyDescription = (key) => CONTINGENCY_BY_KEY[key]?.description || '';

/**
 * Group the given catalog entries into ordered, non-empty display sections.
 *
 * @param {Array<Object>} options - Catalog entries from `getContingencyOptions`.
 * @returns {Array<{key: string, label: string, options: Array<Object>}>}
 */
export const groupContingencyOptions = (options) =>
  CONTINGENCY_GROUPS.map((group) => ({
    ...group,
    options: options.filter((entry) => entry.group === group.key),
  })).filter((group) => group.options.length > 0);

// ── Server refusals (422 `offer_contingencies_invalid`) ──────────────────

export const CONTINGENCY_ELECTION_ERROR_CODE = 'offer_contingencies_invalid';

/**
 * The server's refusal of an offer's contingency elections, from an API error
 * (`err.detail` = `{code, message, paperwork_category, fields,
 * errors: [{loc, msg, type, copy_key?}]}`), or null for any other error.
 *
 * Each entry's text is legal's copy for its `copy_key` when there is one
 * (e.g. `WI.offer.contingency_deadline.required`, `WI.offer.lead_paint.missing`),
 * else the server's `msg`. Types include `contingency_deadline_required` /
 * `_invalid`, `lead_paint_election_required` / `_invalid` / `_conflict` /
 * `_not_available`, `seller_cure_right_required` / `_invalid` /
 * `_not_available` and `seller_cure_days_required` / `_invalid` (copy keys
 * `WI.offer.cure.missing`, `WI.offer.cure.days_help`), and
 * `contingency_elections_required` (field
 * `contingencies`, which no single field shows, so the banner lists it).
 *
 * @param {Error|Object|null} err - Rejection from APIAgent.request, or its `detail`.
 * @returns {{message: string, fieldErrors: Object<string, string>}|null}
 *   `fieldErrors` is keyed by field, e.g. `inspection` or `soil_evaluation_days`.
 */
export const parseContingencyElectionError = (err) => {
  const detail = err && typeof err === 'object' && err.detail !== undefined ? err.detail : err;
  if (!detail || typeof detail !== 'object' || detail.code !== CONTINGENCY_ELECTION_ERROR_CODE) return null;
  const fieldErrors = {};
  (Array.isArray(detail.errors) ? detail.errors : []).forEach((entry) => {
    const loc = Array.isArray(entry?.loc) ? entry.loc : [];
    const field = loc.length ? String(loc[loc.length - 1]) : null;
    const text = (entry?.copy_key && copyText(entry.copy_key)) || entry?.msg;
    if (field && text && !fieldErrors[field]) fieldErrors[field] = text;
  });
  return {
    message: detail.message || "Some contingency elections can't be included in this offer.",
    fieldErrors,
  };
};

/**
 * Server field errors that no rendered field can show (their key isn't among
 * `options`), so the banner lists them instead.
 *
 * `lead_paint_election` and `lead_paint_days` count as rendered when the
 * lead-paint election field is shown (`extraKeys` includes `lead_paint`);
 * `seller_cure_right` and `seller_cure_days` when the cure election is shown
 * (`extraKeys` includes `CURE_ELECTION_FIELD`); the loan terms when the
 * financing terms field is shown (`FINANCING_TERMS_FIELD`); the title periods
 * when the title periods field is shown (`TITLE_PERIODS_FIELD`).
 *
 * @param {Object<string, string>} fieldErrors
 * @param {Array<Object>} options - Catalog entries as rendered.
 * @param {string[]} [extraKeys] - Other contingency keys with a rendered field.
 * @returns {string[]} Messages, without repeats.
 */
export const unplacedFieldErrors = (fieldErrors = {}, options = [], extraKeys = []) => {
  const rendered = new Set([...options.map((o) => o.key), ...extraKeys]);
  const keyOf = (field) => {
    if (field === LEAD_PAINT_ELECTION_KEY) return LEAD_PAINT_KEY;
    if (field === SELLER_CURE_RIGHT_KEY || field === SELLER_CURE_DAYS_KEY) return CURE_ELECTION_FIELD;
    if (FINANCING_TERM_KEYS.includes(field)) return FINANCING_TERMS_FIELD;
    if (TITLE_PERIOD_KEYS.includes(field) || LAND_TITLE_KEYS.includes(field)) return TITLE_PERIODS_FIELD;
    return field.endsWith(DEADLINE_SUFFIX) ? field.slice(0, -DEADLINE_SUFFIX.length) : field;
  };
  const messages = Object.entries(fieldErrors)
    .filter(([field]) => !rendered.has(keyOf(field)))
    .map(([, msg]) => msg);
  return [...new Set(messages)];
};

export { CONTINGENCY_CATALOG, CONTINGENCY_KEYS, CONTINGENCY_GROUPS, LEAD_PAINT_BAN_YEAR };
